import { useEffect, useState, useCallback } from "react";
import { Link, Navigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import BackupPanel from "@/components/BackupPanel";
import MCPTokensPanel from "@/components/MCPTokensPanel";

export default function AdminUsers() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [ok, setOk] = useState("");

  // Create user form
  const [showCreate, setShowCreate] = useState(false);
  const [newEmail, setNewEmail] = useState("");
  const [newName, setNewName] = useState("");
  const [newRole, setNewRole] = useState("user");
  const [newPwd, setNewPwd] = useState("");
  const [newForceChange, setNewForceChange] = useState(true);

  // Reset password modal
  const [resetFor, setResetFor] = useState(null); // user obj
  const [resetPwd, setResetPwd] = useState("");
  const [resetForceChange, setResetForceChange] = useState(true);

  const load = useCallback(async () => {
    setErr("");
    try {
      const r = await api.get("/admin/users");
      setUsers(r.data || []);
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (user && user.role !== "admin") return <Navigate to="/" replace />;

  const flash = (msg) => { setOk(msg); setTimeout(() => setOk(""), 2500); };

  const createUser = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      await api.post("/admin/users", {
        email: newEmail.trim().toLowerCase(),
        name: newName.trim(),
        role: newRole,
        password: newPwd,
        force_change: newForceChange,
      });
      setShowCreate(false); setNewEmail(""); setNewName(""); setNewRole("user"); setNewPwd(""); setNewForceChange(true);
      flash("Usuario creado ✓");
      load();
    } catch (e2) {
      setErr(e2?.response?.data?.detail || e2.message);
    } finally { setBusy(false); }
  };

  const submitReset = async (e) => {
    e.preventDefault();
    if (!resetFor) return;
    setBusy(true); setErr("");
    try {
      await api.post(`/admin/users/${resetFor.user_id}/reset-password`, { new_password: resetPwd, force_change: resetForceChange });
      flash(`Contraseña restablecida para ${resetFor.email}`);
      setResetFor(null); setResetPwd(""); setResetForceChange(true);
    } catch (e2) {
      setErr(e2?.response?.data?.detail || e2.message);
    } finally { setBusy(false); }
  };

  const remove = async (u) => {
    if (!window.confirm(`¿Borrar usuario ${u.email}? No se puede deshacer.`)) return;
    setBusy(true); setErr("");
    try {
      await api.delete(`/admin/users/${u.user_id}`);
      flash(`Usuario ${u.email} eliminado`);
      load();
    } catch (e2) {
      setErr(e2?.response?.data?.detail || e2.message);
    } finally { setBusy(false); }
  };

  const toggleRole = async (u) => {
    const next = u.role === "admin" ? "user" : "admin";
    if (!window.confirm(`Cambiar rol de ${u.email} a "${next}"?`)) return;
    setBusy(true); setErr("");
    try {
      await api.patch(`/admin/users/${u.user_id}`, { role: next });
      flash(`Rol actualizado`);
      load();
    } catch (e2) {
      setErr(e2?.response?.data?.detail || e2.message);
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-gb-bg font-serif text-stone-900" data-testid="admin-users-page">
      <main className="mx-auto max-w-5xl px-4 py-8 sm:px-8 sm:py-12">
        <div className="mb-6 flex flex-wrap items-end justify-between gap-3">
          <div>
            <p className="text-[11px] uppercase tracking-[0.4em] text-[#26775E]">Administración</p>
            <h1 className="mt-1 font-display text-3xl">Usuarios</h1>
            <p className="mt-1 text-sm text-stone-500">Gestiona quién puede acceder a la plataforma.</p>
          </div>
          <button onClick={() => setShowCreate((s) => !s)} data-testid="admin-toggle-create"
                  className="rounded-full bg-[#26775E] px-5 py-2 text-[11px] font-semibold uppercase tracking-widest text-white hover:bg-[#1c5c48]">
            {showCreate ? "Cancelar" : "+ Nuevo usuario"}
          </button>
        </div>

        {ok && <p className="mb-4 rounded bg-emerald-50 px-3 py-2 text-sm text-emerald-700" data-testid="admin-ok">{ok}</p>}
        {err && <p className="mb-4 rounded bg-red-50 px-3 py-2 text-sm text-red-700" data-testid="admin-error">{err}</p>}

        {showCreate && (
          <form onSubmit={createUser} className="mb-6 rounded-xl border border-stone-200 bg-white p-4" data-testid="admin-create-form">
            <div className="grid gap-3 md:grid-cols-2">
              <label className="text-sm">
                <span className="block text-[11px] uppercase tracking-[0.3em] text-stone-500">Email</span>
                <input type="email" required value={newEmail} onChange={(e) => setNewEmail(e.target.value)}
                       data-testid="admin-new-email"
                       className="mt-1 w-full rounded-md border border-stone-300 px-3 py-2 outline-none focus:border-[#26775E]" />
              </label>
              <label className="text-sm">
                <span className="block text-[11px] uppercase tracking-[0.3em] text-stone-500">Nombre</span>
                <input type="text" value={newName} onChange={(e) => setNewName(e.target.value)}
                       data-testid="admin-new-name"
                       className="mt-1 w-full rounded-md border border-stone-300 px-3 py-2 outline-none focus:border-[#26775E]" />
              </label>
              <label className="text-sm">
                <span className="block text-[11px] uppercase tracking-[0.3em] text-stone-500">Rol</span>
                <select value={newRole} onChange={(e) => setNewRole(e.target.value)} data-testid="admin-new-role"
                        className="mt-1 w-full rounded-md border border-stone-300 px-3 py-2 outline-none focus:border-[#26775E]">
                  <option value="user">Usuario</option>
                  <option value="admin">Administrador</option>
                </select>
              </label>
              <label className="text-sm">
                <span className="block text-[11px] uppercase tracking-[0.3em] text-stone-500">Contraseña inicial (mín. 8)</span>
                <input type="text" required minLength={8} value={newPwd} onChange={(e) => setNewPwd(e.target.value)}
                       data-testid="admin-new-password"
                       className="mt-1 w-full rounded-md border border-stone-300 px-3 py-2 outline-none focus:border-[#26775E]" />
              </label>
            </div>
            <label className="mt-3 flex items-center gap-2 text-sm">
              <input type="checkbox"
                     checked={newForceChange}
                     onChange={(e) => setNewForceChange(e.target.checked)}
                     data-testid="admin-new-force-change"
                     className="h-4 w-4 accent-[#26775E]" />
              <span>Obligar a cambiar la contraseña en el primer login</span>
            </label>
            <p className="mt-1 text-[11px] text-stone-500">
              {newForceChange
                ? "El usuario será redirigido a cambiar la contraseña al primer login."
                : "El usuario podrá entrar directamente con la contraseña que le pases. Úsalo para bots/integraciones o cuando ya has acordado la contraseña definitiva."}
            </p>
            <button type="submit" disabled={busy} data-testid="admin-create-submit"
                    className="mt-3 rounded-full bg-[#26775E] px-5 py-2 text-[11px] font-semibold uppercase tracking-widest text-white hover:bg-[#1c5c48] disabled:opacity-40">
              {busy ? "Creando…" : "Crear usuario"}
            </button>
          </form>
        )}

        <div className="overflow-x-auto rounded-xl border border-stone-200 bg-white">
          <table className="w-full text-sm" data-testid="admin-users-table">
            <thead className="bg-stone-900 text-[10px] uppercase tracking-widest text-[#FAFAF6]">
              <tr>
                <th className="px-3 py-2 text-left">Email</th>
                <th className="px-3 py-2 text-left">Nombre</th>
                <th className="px-3 py-2 text-left">Rol</th>
                <th className="px-3 py-2 text-left">Pendiente cambio</th>
                <th className="px-3 py-2 text-left">Último login</th>
                <th className="px-3 py-2 text-right">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.user_id} className="border-t border-stone-200 align-middle" data-testid={`admin-user-row-${u.user_id}`}>
                  <td className="px-3 py-2 font-medium">{u.email}</td>
                  <td className="px-3 py-2">{u.name || "—"}</td>
                  <td className="px-3 py-2">
                    <span className={`rounded-full px-2 py-0.5 text-[10px] uppercase tracking-widest ${u.role === "admin" ? "bg-[#26775E]/15 text-[#26775E]" : "bg-stone-200 text-stone-600"}`}>{u.role}</span>
                  </td>
                  <td className="px-3 py-2 text-stone-600">{u.force_password_change ? "Sí" : "—"}</td>
                  <td className="px-3 py-2 text-[12px] text-stone-500">{u.last_login ? new Date(u.last_login).toLocaleString("es-ES") : "—"}</td>
                  <td className="px-3 py-2 text-right">
                    <div className="inline-flex gap-1">
                      <button onClick={() => { setResetFor(u); setResetPwd(""); }} disabled={busy}
                              data-testid={`admin-reset-${u.user_id}`}
                              title="Restablecer contraseña"
                              className="rounded-full bg-stone-200 px-2 py-1 text-[10px] uppercase tracking-widest text-stone-700 hover:bg-stone-300">↻</button>
                      <button onClick={() => toggleRole(u)} disabled={busy || u.user_id === user?.user_id}
                              data-testid={`admin-role-${u.user_id}`}
                              title="Cambiar rol"
                              className="rounded-full bg-stone-200 px-2 py-1 text-[10px] uppercase tracking-widest text-stone-700 hover:bg-stone-300 disabled:opacity-30">⇅</button>
                      <button onClick={() => remove(u)} disabled={busy || u.user_id === user?.user_id}
                              data-testid={`admin-delete-${u.user_id}`}
                              title="Borrar usuario"
                              className="rounded-full bg-stone-200 px-2 py-1 text-[10px] uppercase tracking-widest text-stone-700 hover:bg-red-100 hover:text-red-700 disabled:opacity-30">×</button>
                    </div>
                  </td>
                </tr>
              ))}
              {!users.length && (
                <tr><td colSpan="6" className="px-3 py-6 text-center text-stone-500">No hay usuarios.</td></tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="mt-6 text-[12px] text-stone-500">
          <Link to="/change-password" className="underline hover:text-[#26775E]">Cambiar mi propia contraseña →</Link>
        </div>

        <BackupPanel />
        <MCPTokensPanel />
      </main>

      {resetFor && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="admin-reset-modal">
          <form onSubmit={submitReset} className="w-full max-w-md rounded-xl bg-white p-5">
            <h3 className="font-display text-lg">Restablecer contraseña</h3>
            <p className="mt-1 text-sm text-stone-600">
              Vas a establecer una nueva contraseña para <strong>{resetFor.email}</strong>.
            </p>
            <label className="mt-4 block text-sm">
              <span className="text-[11px] uppercase tracking-[0.3em] text-stone-500">Nueva contraseña (mín. 8)</span>
              <input type="text" required minLength={8} value={resetPwd} onChange={(e) => setResetPwd(e.target.value)}
                     data-testid="admin-reset-input" autoFocus
                     className="mt-1 w-full rounded-md border border-stone-300 px-3 py-2 outline-none focus:border-[#26775E]" />
            </label>
            <label className="mt-3 flex items-center gap-2 text-sm">
              <input type="checkbox"
                     checked={resetForceChange}
                     onChange={(e) => setResetForceChange(e.target.checked)}
                     data-testid="admin-reset-force-change"
                     className="h-4 w-4 accent-[#26775E]" />
              <span>Obligar al usuario a cambiarla en el próximo login</span>
            </label>
            <p className="mt-1 text-[11px] text-stone-500">
              {resetForceChange
                ? "El usuario tendrá que cambiar la contraseña la próxima vez que entre."
                : "El usuario podrá entrar directamente con esta contraseña — úsalo cuando ya la habéis acordado."}
            </p>
            <div className="mt-4 flex gap-2">
              <button type="submit" disabled={busy} data-testid="admin-reset-submit"
                      className="flex-1 rounded-full bg-[#26775E] px-4 py-2 text-[11px] font-semibold uppercase tracking-widest text-white hover:bg-[#1c5c48] disabled:opacity-40">
                {busy ? "Guardando…" : "Restablecer"}
              </button>
              <button type="button" onClick={() => setResetFor(null)}
                      className="rounded-full bg-stone-200 px-4 py-2 text-[11px] uppercase tracking-widest text-stone-700 hover:bg-stone-300">
                Cancelar
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
